import { BoxProps } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { Fragment } from "react/jsx-runtime";

import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PartDimensionsModal } from "#modules/quoteRequestLines/view/PartDimensionsModal";
import { Icon } from "#shared/icons/Icon";
import { EllipsisTooltip } from "#shared/view/components/EditableTable/EllipsisTooltip";
import { Chip } from "#shared/view/designSystem/Chip";
import {
  FormatterMaterialParameter,
  getMaterialDimensionsFieldsConfiguration,
  ExtendedPart,
  round,
} from "shared";

import classes from "./chipTruncate.module.css";

type Props = {
  part: (Omit<Partial<ExtendedPart>, "id"> & { id: string }) | null;
  quoteRequestLine?: Omit<QuoteRequestLineDto, "latestPredictionJob"> | null;
  onSubmitPartDimensions?: () => void;
  updatable?: boolean;
};

const getDimensions = (part?: Partial<ExtendedPart> | null) => {
  const fieldsConfig = getMaterialDimensionsFieldsConfiguration(part?.type ?? undefined);

  const material: FormatterMaterialParameter = {
    height: part?.height,
    length: part?.length,
    width: part?.width,
    tubeThickness: part?.tubeThickness,
  };

  const hasAnyDimension = fieldsConfig.some((field) => !!material[field.field]);

  if (!hasAnyDimension) {
    return t("qrl.noDimensions");
  }

  const parts = fieldsConfig
    .map((field) => {
      const value = material[field.field];
      if (value === null || value === undefined) {
        return "-";
      }
      const roundedValue = round(value, 2);
      return `${field.symbol ?? ""}${roundedValue}`;
    })
    .join("x");

  return `${parts}mm`;
};

export const DimensionChip = ({
  part,
  quoteRequestLine,
  onSubmitPartDimensions,
  updatable = false,
  ...props
}: BoxProps & Props) => {
  const [
    partDimensionsModalOpened,
    { open: openPartDimensionsModal, close: closePartDimensionsModal },
  ] = useDisclosure(false);

  const canDisplayModal = quoteRequestLine && updatable;

  const handleOpenPartDimensionsModal = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!canDisplayModal) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    openPartDimensionsModal();
  };

  const text = getDimensions(part);

  return (
    <Fragment>
      <Chip
        size="xs"
        icon={<Icon name="ruler" size={14} />}
        checked
        color="blue.4"
        autoContrast
        onClick={(e) => handleOpenPartDimensionsModal(e)}
        classNames={{ label: classes.label }}
        {...props}
      >
        <EllipsisTooltip content={text} />
      </Chip>

      {canDisplayModal && (
        <PartDimensionsModal
          part={part}
          quoteRequestLine={quoteRequestLine}
          partDimensionsModalOpened={partDimensionsModalOpened}
          closePartDimensionsModal={closePartDimensionsModal}
          onSubmit={onSubmitPartDimensions}
        />
      )}
    </Fragment>
  );
};
